/**
 * モーショングラフィックス (プロンプト入力モード) のレイヤーの名前上書きと固定 (motion-graphics-mode-plan.md §3.3・§5.2)。
 * 素材 id ごとに持ち、この store が正本。
 *
 * 方針 (2026-09-30 決定): レイヤー名は素材 (generation_metadata.layer_stack) には保存せず、固定と同じく
 * 生成リクエスト側の値にする。
 * generate の body には `layer_names` (index → 名前の上書き・元の名前と同じなら送らない) / `locked_layer_indexes` として載せる
 * (features/motion/layeredGenerate.ts の buildMotionLayeredRequest)。
 *
 * リロードで消えないよう sessionStorage に永続化する (選択済み素材と同じ寿命)。素材が選択から外れても
 * 古い id のエントリは掃除しない (タブを閉じれば消える・件数は選んだ素材の数だけで小さい)。
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const MOTION_LAYER_EDITS_STORAGE_KEY = 'motion_layer_edits_v1';

type LayerEditsState = {
  /** materialId → (layer index → 利用者が付けた名前) */
  names: Record<string, Record<number, string>>;
  /** materialId → 固定するレイヤーの index (昇順・重複なし) */
  locked: Record<string, number[]>;
  /** name が null なら上書きを消して元の名前に戻す */
  renameLayer: (materialId: string, index: number, name: string | null) => void;
  toggleLayerLock: (materialId: string, index: number) => void;
  /** 1 素材分の名前上書き・固定を消す (レイヤー構成を作り直して番号が変わったとき) */
  clearMaterial: (materialId: string) => void;
  reset: () => void;
};

export const useMotionLayerEditsStore = create<LayerEditsState>()(
  persist(
    (set) => ({
      names: {},
      locked: {},
      renameLayer: (materialId, index, name) => set((s) => {
        const forMaterial = { ...(s.names[materialId] ?? {}) };
        if (name === null) delete forMaterial[index];
        else forMaterial[index] = name;
        return { names: { ...s.names, [materialId]: forMaterial } };
      }),
      toggleLayerLock: (materialId, index) => set((s) => {
        const current = s.locked[materialId] ?? [];
        const next = current.includes(index)
          ? current.filter((i) => i !== index)
          : [...current, index].sort((a, b) => a - b);
        return { locked: { ...s.locked, [materialId]: next } };
      }),
      clearMaterial: (materialId) => set((s) => {
        const names = { ...s.names };
        const locked = { ...s.locked };
        delete names[materialId];
        delete locked[materialId];
        return { names, locked };
      }),
      reset: () => set({ names: {}, locked: {} }),
    }),
    {
      name: MOTION_LAYER_EDITS_STORAGE_KEY,
      // sessionStorage が使えない環境 (プライベートモード等) では永続化せず画面内だけで持つ
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ names: s.names, locked: s.locked }),
    },
  ),
);

/** 名前の入力を確定値にする。前後の空白を落とし、空なら null (= 元の名前に戻す)。80 文字まで */
export function normalizeLayerName(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, 80);
}
